import { FormControlLabel, Switch } from '@mui/material';
import {
  ConductVisibility,
  FilterKeys,
  GetConductEntriesQueryFilters,
  SchoolYear,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  AgeGroupSelectMultiple,
  ConductTypeOptionsSelectMultiple,
  ConductVisibilitySelectMultiple,
  FiltersSidebar,
  FiltersSidebarMethods,
  GroupSelectMultiple,
  PropertyTypeSelectMultiple,
  SchoolPeriodsRangeSelect,
  SchoolPeriodsRangeSelectComponent,
  toggleMultipleValueArrayProperty,
  useFiltersSidebar,
  UserSelectMultiple,
} from '@schooly/components/filters';
import { PROPERTIES_TEXT_IDS, SchoolPropertyType, SchoolUserRole } from '@schooly/constants';
import { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useConductFilters } from './useConductFilters';

export type GroupByOption = FilterKeys.Student | null;

type ConductFiltersSidebarProps = {
  filters: GetConductEntriesQueryFilters;
  defaultFilters: GetConductEntriesQueryFilters;
  schoolId: string;
  onApply: (filters: GetConductEntriesQueryFilters, groupBy: GroupByOption) => void;
  defaultSchoolYear?: SchoolYear;
  groupBy: GroupByOption;
};

export const ConductFiltersSidebar = forwardRef<FiltersSidebarMethods, ConductFiltersSidebarProps>(
  (
    {
      filters: propsFilters,
      defaultFilters,
      onApply,
      schoolId,
      defaultSchoolYear,
      groupBy: propsGroupBy,
    },
    ref,
  ) => {
    const [filters, setFilters] = useState(propsFilters);
    const [groupBy, setGroupBy] = useState(propsGroupBy);
    const { permissions } = useAuth();
    const periodsRef = useRef<SchoolPeriodsRangeSelectComponent>(null);

    const { allowClose, preventClose, sidebar } = useFiltersSidebar(ref);
    const {
      labels,
      hasHouses,
      filtersHouse,
      filtersAgeGroup,
      filtersGroup,
      filtersStudent,
      filtersConductType,
      filtersVisibility,
      filtersConductSelectTypes,
      onSetDate,
      onSetConductTypeId,
      onSelectConductStatus,
      onClearConductStatus,
      onSetConductTypeOption,
      onSetStudentId,
      onClearConductType,
      onSetHouseId,
      onClearHouse,
      onSetAgeGroup,
      onClearAgeGroup,
      onSetGroup,
      onClearGroup,

      onClearStudent,
    } = useConductFilters({ schoolId, filters, onSetFilters: setFilters });

    const { formatMessage } = useIntl();
    const isGroupViewer = permissions.includes('group_viewer');

    const handleApply = useCallback(() => {
      onApply(filters, groupBy);
    }, [filters, onApply, groupBy]);

    const handleClear = useCallback(() => {
      setGroupBy(null);
      setFilters(defaultFilters);
      if (defaultFilters.date) {
        periodsRef.current?.updateDates(defaultFilters.date);
      }
    }, [defaultFilters]);

    const onSelectConductStatusCallback = useCallback(
      (status: ConductVisibility) => {
        onSelectConductStatus(toggleMultipleValueArrayProperty(filtersVisibility, [status]));
      },
      [filtersVisibility, onSelectConductStatus],
    );

    const onSelectAgeGroupCallback = useCallback(
      (ageGroups: string[]) => {
        onSetAgeGroup(toggleMultipleValueArrayProperty(filtersAgeGroup, ageGroups));
      },
      [filtersAgeGroup, onSetAgeGroup],
    );

    const dateFilter = useMemo(
      () => ({ [FilterKeys.IntersectDate]: filters.date }),
      [filters.date],
    );

    return (
      <FiltersSidebar
        onOpen={() => {
          setFilters(propsFilters);
          setGroupBy(propsGroupBy);
        }}
        ref={sidebar}
        title={formatMessage(
          { id: 'filter-FiltersForPage' },
          { page: formatMessage({ id: 'section-Conduct' }) },
        )}
        onClear={handleClear}
        onApply={handleApply}
      >
        <SchoolPeriodsRangeSelect
          ref={periodsRef}
          showSelectedPeriodLabel={false}
          schoolId={schoolId}
          defaultSchoolYear={defaultSchoolYear}
          hasManagePermission={permissions.includes('school_admin')}
          onSetDate={onSetDate}
          placeholder={labels[FilterKeys.Date]}
          date={filters.date || []}
          requiredLabel="required"
          onOpen={preventClose}
          onClose={allowClose}
        />

        <ConductVisibilitySelectMultiple
          placeholder={labels[FilterKeys.ConductStatus]}
          onSelectConductVisibility={onSelectConductStatusCallback}
          selectedVisibility={filtersVisibility || []}
          onClear={onClearConductStatus}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <ConductTypeOptionsSelectMultiple
          placeholder={labels[FilterKeys.ConductType]}
          schoolId={schoolId}
          selectedConductTypeIds={filtersConductType || []}
          selectedConductTypeOptions={filtersConductSelectTypes || []}
          onSelectConductTypeId={onSetConductTypeId}
          onSelectConductTypeOption={onSetConductTypeOption}
          onClear={onClearConductType}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <UserSelectMultiple
          type="student"
          placeholder={labels[FilterKeys.Student]}
          schoolId={schoolId}
          onSelectUserId={(id) =>
            onSetStudentId(toggleMultipleValueArrayProperty(filtersStudent, [id]))
          }
          selectedIds={filtersStudent || []}
          onClear={onClearStudent}
          filters={{ [FilterKeys.Date]: filters.date }}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <AgeGroupSelectMultiple
          allowArchived
          userRole={SchoolUserRole.Student}
          placeholder={labels[FilterKeys.AgeGroup]}
          schoolId={schoolId}
          onSelect={onSelectAgeGroupCallback}
          selectedIds={filtersAgeGroup || []}
          onClear={onClearAgeGroup}
          onOpen={preventClose}
          onClose={allowClose}
        />

        {hasHouses && (
          <PropertyTypeSelectMultiple
            placeholder={labels[FilterKeys.House]}
            allowArchived
            userRole={SchoolUserRole.Student}
            schoolId={schoolId}
            onSelectId={(v: string) =>
              onSetHouseId(toggleMultipleValueArrayProperty(filtersHouse, [v]))
            }
            selectedIds={filtersHouse || []}
            onClear={onClearHouse}
            propertyType={SchoolPropertyType.House}
            onOpen={preventClose}
            onClose={allowClose}
          />
        )}

        {isGroupViewer && (
          <GroupSelectMultiple
            placeholder={labels[FilterKeys.Group]}
            schoolId={schoolId}
            onSelectGroupId={(v: string) =>
              onSetGroup(toggleMultipleValueArrayProperty(filtersGroup, [v]))
            }
            selectedIds={filtersGroup || []}
            onClear={onClearGroup}
            filters={dateFilter}
            onOpen={preventClose}
            onClose={allowClose}
          />
        )}
        <FormControlLabel
          sx={{
            px: 1.5,
          }}
          control={
            <Switch
              checked={groupBy === FilterKeys.Student}
              onChange={() =>
                setGroupBy(groupBy === FilterKeys.Student ? null : FilterKeys.Student)
              }
            />
          }
          label={`${formatMessage({ id: 'filter-GroupBy' })} ${formatMessage({
            id: PROPERTIES_TEXT_IDS['student'],
          })}`}
        />
      </FiltersSidebar>
    );
  },
);
